import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowUpRight, Check } from "lucide-react";
import { SiteHeader } from "@/components/SiteHeader";
import { PartnerForm } from "@/components/PartnerForm";
import { SiteFooter } from "@/components/SiteFooter";

const TITLE = "Partnerprogramm: 10 % Provision für Shopify-Projekte";
const DESCRIPTION =
  "Für SEO-Freelancer, Berater und Dienstleister: 10 % Provision auf die Netto-Auftragssumme, 399 € bis 1.890 € pro vermitteltem Shopify-Projekt. Gilt auch für Entwicklungs- und Umsetzungs-Tasks.";
const URL = "https://shopify-freelancer.net/partner-werden";

const steps = [
  {
    no: "01",
    title: "Kunden vorstellen",
    text: "Sie nennen mir Shop und Ansprechpartner, oder stellen uns per E-Mail vor. Wichtig ist nur, dass die Vermittlung vor dem Erstkontakt bei mir liegt.",
  },
  {
    no: "02",
    title: "Ich prüfe den Case",
    text: "Der Shop bekommt eine kostenlose Ersteinschätzung. Passt das Projekt gerade nicht, sage ich das offen, Ihrem Kunden gegenüber und Ihnen.",
  },
  {
    no: "03",
    title: "Auftrag kommt zustande",
    text: "Kommt es zum Projekt, arbeite ich direkt mit dem Kunden. Sie bleiben informiert, koordinieren aber nichts und tragen keine Verantwortung für die Umsetzung.",
  },
  {
    no: "04",
    title: "Provision wird ausgezahlt",
    text: "10 % der Netto-Auftragssumme, ohne Deckel. Auszahlung innerhalb von 14 Tagen nach Zahlungseingang bei mir, gegen Ihre Rechnung.",
  },
];

const examples = [
  { label: "Sprint", order: "3.990 €", fee: "399 €" },
  { label: "Programm", order: "9.900 €", fee: "990 €" },
  { label: "Partnerschaft", order: "18.900 €", fee: "1.890 €" },
];

const fitFor = [
  "SEO-Freelancer, die Traffic liefern, aber nicht am Store schrauben",
  "Ads-Dienstleister, deren Kampagnen an der Kaufabschlussrate hängen",
  "Web- und Branding-Studios ohne eigenes Shopify-Team",
  "Berater mit E-Commerce-Mandanten und offenen Umsetzungs-Tasks",
];

const rules = [
  "Vermittlung gilt 6 Monate ab Vorstellung",
  "Provision bezieht sich auf den ersten vermittelten Auftrag des Kunden",
  "Provision gibt es nur bei bezahltem Auftrag, keine Vorschüsse",
  "Gilt für alle Shopify-Projekte, nicht nur Conversion-Optimierung",
  "Ich pitche nicht gegen Sie und übernehme Ihre Leistung nicht",
];

const faqs = [
  {
    q: "Wie hoch ist die Provision genau?",
    a: "10 % der Netto-Auftragssumme, ohne Deckel. Beim Sprint für 3.990 € sind das 399 €, beim Programm für 9.900 € sind es 990 €, bei der Partnerschaft für 18.900 € sind es 1.890 €.",
  },
  {
    q: "Gilt die Provision nur für Conversion-Optimierung?",
    a: "Nein. Die Provision gilt für alle Shopify-Projekte. Braucht Ihr Kunde einen Shopify-Entwickler oder haben Sie einen konkreten Task, sprechen Sie mich gern an, abgerechnet wird identisch mit 10 % der Netto-Auftragssumme.",
  },
  {
    q: "Wann wird ausgezahlt?",
    a: "Innerhalb von 14 Tagen nach Zahlungseingang des Kunden, gegen Ihre Rechnung. Bei Ratenzahlung anteilig zur jeweiligen Rate.",
  },
  {
    q: "Gilt die Provision auch für Folgeaufträge?",
    a: "Nein. Die Provision bezieht sich auf den ersten vermittelten Auftrag des Kunden.",
  },

  {
    q: "Muss ich einen Vertrag unterschreiben?",
    a: "Nein. Eine kurze E-Mail mit Shop und Ansprechpartner reicht als Nachweis der Vermittlung. Auf Wunsch bestätige ich die Vereinbarung schriftlich.",
  },
  {
    q: "Nehmen Sie mir meinen Kunden weg?",
    a: "Nein. Ich arbeite an Shopify, Conversion, Umsetzung, Entwicklung. SEO, Ads, Content oder Betreuung bleiben bei Ihnen; ich empfehle Sie aktiv weiter.",
  },
];


export const Route = createFileRoute("/partner-werden")({
  component: PartnerPage,
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:url", content: URL },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [
      { rel: "canonical", href: URL },
      { rel: "alternate", hrefLang: "de", href: "https://shopify-freelancer.net/partner-werden" },
      { rel: "alternate", hrefLang: "en", href: "https://shopify-freelancer.net/en/partner-program" },
      { rel: "alternate", hrefLang: "x-default", href: "https://shopify-freelancer.net/partner-werden" },
      { rel: "alternate", hrefLang: "de", href: URL },
      {
        rel: "alternate",
        hrefLang: "en",
        href: "https://shopify-freelancer.net/en/partner-program",
      },
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: faqs.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }),
      },
    ],
  }),
});

function PartnerPage() {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <SiteHeader />
      <main>
        <section className="veil border-b border-border/60 py-14 md:py-20">
          <div className="mx-auto max-w-3xl px-5">
            <p className="text-xs uppercase tracking-[0.22em] text-muted-foreground">
              Partnerprogramm
            </p>
            <h1 className="mt-5 text-4xl font-bold leading-tight md:text-5xl">
              Shopify-Projekte vermitteln 
              <br />
              bis zu <span className="text-signal">1.890 €</span> Provision.
            </h1>
            <p className="mt-6 leading-relaxed text-muted-foreground">
              Sie betreuen E-Commerce-Kunden, decken aber die Umsetzung im Store nicht ab?
              Vermitteln Sie das Projekt an mich. Kommt der Auftrag zustande, erhalten Sie 10 %
              der Netto-Auftragssumme, ohne Deckel, 399 € bis 1.890 € pro Projekt. Das
              gilt nicht nur für Conversion-Optimierung: Braucht Ihr Kunde einen
              Shopify-Entwickler oder haben Sie einen konkreten Task, sprechen Sie mich gern an.
              Kein Vertrag, keine Exklusivität, kein Aufwand für Sie.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <a
                href="#empfehlen"
                className="shadow-signal inline-flex items-center gap-2 rounded-full bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground transition-transform hover:-translate-y-0.5"
              >
                Projekt vermitteln
                <ArrowUpRight className="h-4 w-4" />
              </a>

              <Link
                to="/cases"
                className="inline-flex items-center gap-2 rounded-full border border-border px-6 py-3 text-sm font-semibold hover:border-primary"
              >
                Ergebnisse ansehen
              </Link>
            </div>
          </div>
        </section>

        <section className="border-b border-border/60 py-16">
          <div className="mx-auto max-w-6xl px-5">
            <h2 className="text-3xl font-bold md:text-4xl">Was Sie bekommen</h2>
            <div className="mt-10 grid gap-5 md:grid-cols-3">
              {examples.map((e) => (
                <div
                  key={e.label}
                  className="shadow-lift rounded-lg border border-border bg-card p-6"
                >
                  <p className="text-sm text-muted-foreground">{e.label}</p>
                  <p className="mt-3 text-sm text-muted-foreground">
                    Auftragssumme {e.order}
                  </p>
                  <p className="text-signal font-display mt-1 text-3xl font-bold">
                    {e.fee}
                  </p>
                </div>
              ))}
            </div>
            <p className="mt-6 text-sm text-muted-foreground">
              10 % der Netto-Auftragssumme jedes Pakets, ohne Deckel, auch bei reinen
              Entwicklungs- und Umsetzungs-Tasks. Die Provision bezieht sich auf den ersten
              vermittelten Auftrag des Kunden.
            </p>

          </div>
        </section>

        <section className="border-b border-border/60 py-16">
          <div className="mx-auto max-w-6xl px-5">
            <h2 className="text-3xl font-bold md:text-4xl">So läuft es ab</h2>
            <div className="mt-10 grid gap-5 md:grid-cols-4">
              {steps.map((s) => (
                <div key={s.no} className="rounded-lg border border-border bg-card p-6">
                  <span className="text-signal font-display text-4xl font-bold">
                    {s.no}
                  </span>
                  <h3 className="mt-4 text-base font-semibold">{s.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                    {s.text}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="border-b border-border/60 py-16">
          <div className="mx-auto grid max-w-6xl gap-10 px-5 md:grid-cols-2">
            <div>
              <h2 className="text-3xl font-bold md:text-4xl">Für wen sich das lohnt</h2>
              <ul className="mt-6 space-y-3">
                {fitFor.map((f) => (
                  <li key={f} className="flex items-start gap-2.5 text-sm">
                    <Check className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                    {f}
                  </li>
                ))}
              </ul>
              <p className="mt-6 text-sm leading-relaxed text-muted-foreground">
                Ihre Kampagne oder Ihr Ranking bringt mehr Besucher, aber der Store
                macht daraus keine Käufe. Genau da setze ich an. Und wenn Ihr Kunde
                stattdessen einen Shopify-Entwickler für einen konkreten Task braucht:
                sprechen Sie mich gern an, die Provision gilt genauso. Sie behalten Ihren Kunden,
                Ihr Ergebnis sieht besser aus, und Sie verdienen an der Vermittlung mit.
              </p>

            </div>
            <div className="rounded-xl border border-border bg-card p-6 md:p-8">
              <h3 className="text-lg font-semibold">Die Spielregeln</h3>
              <ul className="mt-5 space-y-3">
                {rules.map((r) => (
                  <li key={r} className="flex items-start gap-2.5 text-sm">
                    <Check className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                    {r}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        <section className="border-b border-border/60 py-16">
          <div className="mx-auto max-w-3xl px-5">
            <h2 className="text-3xl font-bold md:text-4xl">Häufige Fragen</h2>
            <div className="mt-8 space-y-5">
              {faqs.map((f) => (
                <div key={f.q} className="rounded-lg border border-border bg-card p-6">
                  <h3 className="text-base font-semibold">{f.q}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                    {f.a}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section id="empfehlen" className="veil border-t border-border/60 py-16 md:py-20">
          <div className="mx-auto grid max-w-5xl gap-10 px-5 md:grid-cols-2 md:items-center">
            <div>
              <h2 className="text-3xl font-bold md:text-4xl">Projekt vermitteln</h2>
              <p className="mt-5 text-muted-foreground">
                Schicken Sie mir die Shop-URL des Kunden, Ihren Namen und, falls vorhanden 
                den konkreten Task. Ich bestätige die Vermittlung schriftlich und melde
                mich innerhalb von 48 Stunden mit einer belastbaren Ersteinschätzung.
                Kommt der Auftrag zustande, erhalten Sie 10 % der Netto-Auftragssumme.
              </p>

            </div>
            <PartnerForm />
          </div>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}
